// The placeholder as a first-class atomic node (FR-006).
//
// `atom: true` + `inline: true` is what makes the requirement true rather than
// simulated: ProseMirror treats the node as a single indivisible unit, so one
// Backspace removes the whole placeholder rather than a character of its label,
// and no content can be typed inside it.
//
// It also means the stored document carries a typed node — not styled text that
// has to be re-parsed — which is what lets the same schema validate on the
// server (research.md D-01).

import { Node, mergeAttributes } from '@tiptap/core';

export interface PlaceholderOptions {
  HTMLAttributes: Record<string, unknown>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    placeholderField: {
      insertPlaceholder: (name: string) => ReturnType;
      renamePlaceholderEverywhere: (oldName: string, newName: string) => ReturnType;
    };
  }
}

export const PlaceholderNode = Node.create<PlaceholderOptions>({
  name: 'placeholder',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return { HTMLAttributes: {} };
  },

  addAttributes() {
    return {
      name: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-placeholder-name'),
        renderHTML: (attributes) => ({
          'data-placeholder-name': attributes['name'] as string,
        }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-placeholder-name]' }];
  },

  renderHTML({ HTMLAttributes, node }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        class: 'placeholder-chip',
        contenteditable: 'false',
      }),
      // Text content, not markup — the name is user-supplied.
      String(node.attrs['name'] ?? ''),
    ];
  },

  addCommands() {
    return {
      insertPlaceholder:
        (name: string) =>
        ({ commands }) =>
          commands.insertContent({ type: this.name, attrs: { name } }),

      // FR-006a: every occurrence renames together, or the template would
      // silently gain a second form field.
      renamePlaceholderEverywhere:
        (oldName: string, newName: string) =>
        ({ tr, state, dispatch }) => {
          let changed = false;
          state.doc.descendants((node, pos) => {
            if (node.type.name === this.name && node.attrs['name'] === oldName) {
              tr.setNodeMarkup(pos, undefined, { ...node.attrs, name: newName });
              changed = true;
            }
          });
          if (changed && dispatch) dispatch(tr);
          return changed;
        },
    };
  },
});
